/**
 * HTTP utilities with Result pattern for type-safe API calls
 */

import type { Result } from "@/types/layout";
import { getEnv } from "@/config/env";
import { getLocale } from "@/utils/locale-store";
import { SESSION_ENDED_CODE, handleSsoSessionEnded } from "@/utils/sso-session";
import { isPreviewMode, previewHeaders } from "@/utils/preview";
import { toSafeRelativePath } from "@/utils/url";

interface FetchOptions extends RequestInit {
  timeout?: number;
}

interface GetFetchOptions extends FetchOptions {
  retries?: number;
  retryDelay?: number;
}

const DEFAULT_TIMEOUT = 10000;

/**
 * Appends `&nocache` to a URL when the page is in preview mode,
 * ensuring the backend skips its output cache and returns fresh content.
 */
export function withCacheBust(url: string): string {
  if (!isPreviewMode()) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}nocache`;
}

/**
 * Standard headers every API call must send so the BE can resolve the user's
 * store, language, country, and currency context. Exported so callers that
 * build their own fetch (e.g. when they need to parse a non-standard response
 * body shape like ProblemDetails) can stay aligned with getJson/postJson.
 *
 * In preview mode this also carries the backoffice ticket (X-Preview-Token),
 * which is what makes the backend answer with DRAFT content — see utils/preview.
 */
export function defaultHeaders(): Record<string, string> {
  const locale = getLocale();
  return {
    SiteId: getEnv().siteId,
    "X-Store-Language": locale.language,
    "X-Store-Country": locale.country,
    "X-Store-Currency": locale.currency,
    ...previewHeaders(),
  };
}

/** Formats an HTTP error string as "<status> <statusText>". Used by isClientError to parse back the status. */
function formatHttpError(status: number, statusText: string): string {
  return `${status} ${statusText}`;
}

/** The ProblemDetails fields this layer acts on, whatever else the body carries. */
interface ProblemDetailsFields {
  /** Same-origin relative path, already sanitized. */
  redirectUrl?: string;
  detail?: string;
  code?: string;
  /** ValidationProblemDetails `errors`, flattened to their messages. */
  validationErrors?: string[];
}

/** Flattens ValidationProblemDetails `errors` ({ field: [messages] }) to its string messages. */
function readValidationErrors(errors: unknown): string[] | undefined {
  if (typeof errors !== "object" || errors === null) return undefined;
  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .filter((value): value is string => typeof value === "string");
  return messages.length > 0 ? messages : undefined;
}

/**
 * Reads a non-OK response body once and acts on the parts of it that are the
 * fetch layer's business: a session that has ended, and a redirect target.
 * A body can only be read once, so every verb must go through here rather than
 * calling response.json() itself.
 */
async function readErrorResponse(
  response: Response
): Promise<ProblemDetailsFields> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) return {};

  let body: {
    redirectUrl?: unknown;
    detail?: unknown;
    code?: unknown;
    errors?: unknown;
  };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    /* non-JSON or unreadable body — nothing to act on */
    return {};
  }

  const fields: ProblemDetailsFields = {
    redirectUrl: toSafeRelativePath(body?.redirectUrl),
    detail:
      typeof body?.detail === "string" && body.detail.trim()
        ? body.detail
        : undefined,
    code: typeof body?.code === "string" ? body.code : undefined,
    validationErrors: readValidationErrors(body?.errors),
  };

  // Handled here rather than per hook: the member's SSO session has ended, so
  // every call this page makes will fail the same way and only the first one
  // needs to start the navigation.
  if (response.status === 401 && fields.code === SESSION_ENDED_CODE) {
    handleSsoSessionEnded();
  }

  return fields;
}

/**
 * Reads a successful response body as JSON, treating 204 No Content and
 * Content-Length: 0 as "no body" and returning null. The unconditional
 * response.json() it replaces throws "Unexpected end of JSON input" on an
 * empty body, which surfaced as a fake error after 204-returning mutations
 * (cancel-subscription, delete address, set default address).
 */
async function parseJsonBody<T>(response: Response): Promise<T> {
  if (response.status === 204) return null as T;
  if (response.headers.get("Content-Length") === "0") return null as T;
  return (await response.json()) as T;
}

/** Matches the status code from a string produced by formatHttpError. */
const HTTP_STATUS_RE = /^(\d{3})\s/;

function isClientError(error: string): boolean {
  const match = error.match(HTTP_STATUS_RE);
  if (!match) return false;
  const status = parseInt(match[1]!, 10);
  return status >= 400 && status < 500;
}

/** True when an error string produced by formatHttpError carries a 404. */
export function isNotFoundError(error: string): boolean {
  return error.match(HTTP_STATUS_RE)?.[1] === "404";
}

function retryDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}

/**
 * Performs a single GET request (no retry).
 */
async function singleGetJson<T>(
  url: string,
  timeout: number,
  externalSignal: AbortSignal | undefined,
  fetchInit: RequestInit
): Promise<Result<T>> {
  if (externalSignal?.aborted) {
    return { ok: false, error: "Request aborted" };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const onAbort = () => controller.abort();
  externalSignal?.addEventListener("abort", onAbort);

  try {
    const response = await fetch(url, {
      ...fetchInit,
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...defaultHeaders(),
        ...(fetchInit.headers || {}),
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    externalSignal?.removeEventListener("abort", onAbort);

    if (!response.ok) {
      const { redirectUrl } = await readErrorResponse(response);
      return {
        ok: false,
        error: formatHttpError(response.status, response.statusText),
        redirectUrl,
      };
    }

    const data = await parseJsonBody<T>(response);
    return { ok: true, data };
  } catch (error) {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener("abort", onAbort);

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        return { ok: false, error: "Request aborted" };
      }
      return { ok: false, error: error.message };
    }

    return { ok: false, error: "Unknown error occurred" };
  }
}

/**
 * Performs a GET request with automatic retry for transient errors.
 * Retries on network errors, timeouts, and 5xx responses.
 * Does NOT retry on aborted requests or 4xx client errors.
 */
export async function getJson<T>(
  url: string,
  init?: GetFetchOptions
): Promise<Result<T>> {
  const {
    timeout = DEFAULT_TIMEOUT,
    signal: externalSignal,
    retries = 2,
    retryDelay: delay = 1000,
    ...fetchInit
  } = init ?? {};

  let attemptsLeft = retries + 1;
  let currentDelay = delay;

  while (attemptsLeft > 0) {
    attemptsLeft--;

    if (externalSignal?.aborted) {
      return { ok: false, error: "Request aborted" };
    }

    const result = await singleGetJson<T>(
      url,
      timeout,
      externalSignal ?? undefined,
      fetchInit
    );

    if (result.ok) return result;
    if (result.error === "Request aborted") return result;
    if (isClientError(result.error)) return result;
    if (attemptsLeft === 0) return result;

    await retryDelay(currentDelay, externalSignal ?? undefined);
    currentDelay *= 2;
  }

  // istanbul ignore next -- unreachable: loop always returns when attemptsLeft === 0
  return { ok: false, error: "Unknown error occurred" };
}

/**
 * Performs a POST request with JSON body and returns typed JSON data
 */
export async function postJson<T>(
  url: string,
  body: unknown,
  init?: FetchOptions
): Promise<Result<T>> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchInit } = init ?? {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchInit,
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...defaultHeaders(),
        ...(fetchInit.headers || {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const { validationErrors } = await readErrorResponse(response);
      return {
        ok: false,
        error: formatHttpError(response.status, response.statusText),
        ...(validationErrors ? { validationErrors } : {}),
      };
    }

    const data = await parseJsonBody<T>(response);
    return { ok: true, data };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        return { ok: false, error: "Request timeout" };
      }
      return { ok: false, error: error.message };
    }

    return { ok: false, error: "Unknown error occurred" };
  }
}

/**
 * Performs a POST request like postJson, but surfaces the ProblemDetails
 * `detail` field from error-response bodies as the Result error, so the UI
 * can show the backend's message. postJson discards error bodies.
 */
export async function postJsonWithProblemDetails<T>(
  url: string,
  body: unknown
): Promise<Result<T>> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...defaultHeaders(),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const { detail } = await readErrorResponse(response);
      return {
        ok: false,
        error: detail ?? formatHttpError(response.status, response.statusText),
      };
    }

    if (response.status === 204) return { ok: true, data: null as T };
    return { ok: true, data: (await response.json()) as T };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        return { ok: false, error: "Request timeout" };
      }
      return { ok: false, error: error.message };
    }
    return { ok: false, error: "Unknown error occurred" };
  }
}

/**
 * Performs a PUT request with JSON body and returns typed JSON data
 */
export async function putJson<T>(
  url: string,
  body: unknown,
  init?: FetchOptions
): Promise<Result<T>> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchInit } = init ?? {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchInit,
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...defaultHeaders(),
        ...(fetchInit.headers || {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      await readErrorResponse(response);
      return {
        ok: false,
        error: formatHttpError(response.status, response.statusText),
      };
    }

    const data = await parseJsonBody<T>(response);
    return { ok: true, data };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        return { ok: false, error: "Request timeout" };
      }
      return { ok: false, error: error.message };
    }

    return { ok: false, error: "Unknown error occurred" };
  }
}

/**
 * Performs a DELETE request with optional JSON body and returns typed JSON data
 */
export async function deleteJson<T>(
  url: string,
  body?: unknown,
  init?: FetchOptions
): Promise<Result<T>> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchInit } = init ?? {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchInit,
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...defaultHeaders(),
        ...(fetchInit.headers || {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      await readErrorResponse(response);
      return {
        ok: false,
        error: formatHttpError(response.status, response.statusText),
      };
    }

    const data = await parseJsonBody<T>(response);
    return { ok: true, data };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === "AbortError") {
        return { ok: false, error: "Request timeout" };
      }
      return { ok: false, error: error.message };
    }

    return { ok: false, error: "Unknown error occurred" };
  }
}
